import React, { useState, useEffect } from 'react';
import { Button } from 'semantic-ui-react';
import { formatFileSize, deleteDocumentFromDataset, checkDocumentTrainingStatus } from '../services/fileUploadService';

const FilesTab = ({ 
  showToaster,
  datasetId,
  uploadedFiles, // Add this to detect when uploads happen
  onDocumentCountChange // Add this to update document count in navbar
}) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingFiles, setDeletingFiles] = useState(new Set());
  const pollingIntervalRef = React.useRef(null);
  
  // Utility function to get datasetId reliably
  const getDatasetId = () => {
    if (datasetId) {
      return datasetId;
    }
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('c');
  };
  
  // Load documents from API (with loading indicator for initial loads)
  const loadDocuments = async (showLoadingIndicator = true) => {
    const currentDatasetId = getDatasetId();
    if (!currentDatasetId) return;
    
    if (showLoadingIndicator) {
      setLoading(true);
    }
    
    try {
      console.log('FilesTab: Loading documents from API for dataset:', currentDatasetId);
      const apiDocuments = await checkDocumentTrainingStatus(currentDatasetId);
      console.log('FilesTab: API response:', apiDocuments);
      
      setDocuments(apiDocuments || []);
      
      // Update document count in navbar
      if (onDocumentCountChange) {
        onDocumentCountChange((apiDocuments || []).length);
      }
      
      // Check if polling is needed
      checkAndManagePolling(apiDocuments || []);
      
    } catch (error) {
      console.error('FilesTab: Failed to load documents from API:', error);
      setDocuments([]);
      
      // Update document count to 0 on error
      if (onDocumentCountChange) {
        onDocumentCountChange(0);
      }
    } finally {
      if (showLoadingIndicator) {
        setLoading(false);
      }
    }
  };
  
  // Check if we need to start or stop polling based on document statuses
  const checkAndManagePolling = (docs) => {
    const hasInProgressDocs = docs.some(doc => 
      doc.trainingStatus === 'Queued' || 
      doc.trainingStatus === 'Training' || 
      doc.trainingStatus === 'Deleting' ||
      deletingFiles.has(doc.id)
    );
    
    if (hasInProgressDocs && !pollingIntervalRef.current) {
      console.log('FilesTab: Starting polling - documents in progress');
      startPolling();
    } else if (!hasInProgressDocs && pollingIntervalRef.current) {
      console.log('FilesTab: Stopping polling - no documents in progress');
      stopPolling();
    }
  };
  
  // Start polling every 5 seconds (no loading indicator for polling updates)
  const startPolling = () => {
    if (pollingIntervalRef.current) return; // Already polling
    
    pollingIntervalRef.current = setInterval(async () => {
      // Use loadDocuments without loading indicator for polling
      const currentDatasetId = getDatasetId();
      if (!currentDatasetId) return;
      
      try {
        console.log('FilesTab: Polling for document updates...');
        const apiDocuments = await checkDocumentTrainingStatus(currentDatasetId);
        setDocuments(apiDocuments || []);
        
        // Update document count in navbar during polling
        if (onDocumentCountChange) {
          onDocumentCountChange((apiDocuments || []).length);
        }
        
        // Check if we should continue polling
        const hasInProgressDocs = (apiDocuments || []).some(doc => 
          doc.trainingStatus === 'Queued' || 
          doc.trainingStatus === 'Training' || 
          doc.trainingStatus === 'Deleting' ||
          deletingFiles.has(doc.id)
        );
        
        if (!hasInProgressDocs) {
          console.log('FilesTab: All documents completed, stopping polling');
          stopPolling();
        }
      } catch (error) {
        console.error('FilesTab: Polling failed:', error);
      }
    }, 5000);
  };
  
  // Stop polling
  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };
  
  // Load documents every time component mounts (when tab is accessed)
  useEffect(() => {
    console.log('FilesTab: Component mounted, loading documents...');
    loadDocuments();
  }, [datasetId]);
  
  // Watch for new uploads - when uploadedFiles changes, reload from API
  useEffect(() => {
    if (uploadedFiles && uploadedFiles.length > 0) {
      console.log('FilesTab: Detected uploadedFiles change, reloading from API');
      // Don't show loading indicator for upload-triggered reloads since user can see the upload happening
      loadDocuments(false);
    }
  }, [uploadedFiles]);
  
  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);

  const getStatusIcon = (status, isDeleting = false) => {
    if (isDeleting) {
      return <span className="simple-status-icon deleting" title="Deleting document"></span>;
    }
    
    switch (status) {
      case 'queued':
        return <span className="simple-status-icon queued" title="Queued for processing"></span>;
      case 'processing':
        return <span className="simple-status-icon processing" title="Processing document"></span>;
      case 'completed':
        return <span className="simple-status-icon completed" title="Processing completed"></span>;
      case 'failed':
        return <span className="simple-status-icon failed" title="Processing failed"></span>;
      case 'deleting':
        return <span className="simple-status-icon deleting" title="Deleting document"></span>;
      default:
        return <span className="simple-status-icon queued"></span>;
    }
  };

  const getStatusText = (status, isDeleting = false) => {
    if (isDeleting) {
      return 'Deleting';
    }
    
    switch (status) {
      case 'queued':
        return 'Queued';
      case 'processing':
        return 'Processing';
      case 'completed':
        return 'Completed';
      case 'failed':
        return 'Failed';
      case 'deleting':
        return 'Deleting';
      default:
        return 'Unknown';
    }
  };

  const handleDelete = async (document) => {
    const currentDatasetId = getDatasetId();
    
    if (!currentDatasetId) {
      if (showToaster) {
        showToaster('Unable to delete file - missing dataset ID', 'error');
      }
      return;
    }
    
    const documentId = document.id;
    
    if (deletingFiles.has(documentId)) {
      return;
    }
    
    try {
      setDeletingFiles(prev => new Set(prev).add(documentId));
      
      if (showToaster) {
        showToaster('Deleting document...', 'info');
      }
      
      await deleteDocumentFromDataset(documentId, currentDatasetId);
      
      if (showToaster) {
        showToaster(`Document "${document.name}" deleted successfully`, 'success');
      }
      
      // Reload documents from API after deletion (no loading indicator since user can see delete in progress)
      loadDocuments(false);
      
    } catch (error) {
      console.error('Delete operation failed:', error);
      
      if (error.message && (error.message.includes('404') || error.message.includes('Document not found'))) {
        if (showToaster) {
          showToaster(`Document "${document.name}" was already deleted`, 'info');
        }
        // Reload to sync with backend (no loading indicator)
        loadDocuments(false);
      } else {
        if (showToaster) {
          showToaster(error.message || 'Failed to delete document', 'error');
        }
      }
    } finally {
      setDeletingFiles(prev => {
        const newSet = new Set(prev);
        newSet.delete(documentId);
        return newSet;
      });
    }
  };

  if (loading) {
    return (
      <div className="filesTabLeft" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '700px', marginLeft: '170px' }}>
        <div className="files-empty-message">
          <p>Loading documents...</p>
        </div>
      </div>
    );
  }

  if (documents.length === 0) {
    return (
      <div className="filesTabLeft" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '700px', marginLeft: '170px' }}>
        <div className="files-empty-message">
          <p>No documents found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="filesTabLeft">
      {documents.map((doc) => {
        const isDeleting = deletingFiles.has(doc.id);
        
        return (
          <div key={doc.id} className="filesTabOuter">
            <div className="menu-item-search filesTab">
              <div className="file-main-info">
                <div className="file-name" title={doc.name}>
                  {doc.name}
                </div>
                <div className="file-status-inline">
                  {getStatusIcon(doc.status, isDeleting)}
                  <span className="status-text">{getStatusText(doc.status, isDeleting)}</span>
                </div>
              </div>
              <div className="file-meta-line">
                <div className="file-size-date-group">
                  <span className="file-size">{formatFileSize(doc.fileSizeBytes || doc.size || 0)}</span>
                  {doc.createdAt && (
                    <span className="file-date">
                       • {new Date(doc.createdAt).toLocaleDateString()}
                    </span>
                  )}
                </div>
                <div className="file-actions-inline">
                  <Button
                    size="mini"
                    basic
                    className="remove-file-btn-small"
                    onClick={() => handleDelete(doc)}
                    title="Delete document"
                    disabled={isDeleting}
                  >
                    <span className="simple-delete-icon">×</span>
                  </Button>
                </div>
              </div>
              {doc.error && (
                <div className="file-error-line">
                  <span className="simple-status-icon failed"></span>
                  <span>{doc.error}</span>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default FilesTab;